// WWI field hospital: a white canvas hospital tent whose big flat white roof
// panel carries a bold red cross — the roof IS the sign — with a cot in the
// open yard where the wounded are tended. 2x2 tiles.
import { defineModel, standVisit, styleV3, type Part } from "../../lib";

const { box, cyl, slab, poles } = styleV3;

const TENT = "#e9e4d6";        // white canvas walls
const ROOF_W = "#f0ece1";      // the white roof panel — the medical code
const CROSS = "#c0392b";       // the bold red cross
const CANVAS = "#cbbd93";
const TIMBER = "#8b6438";
const SHADOW = "#57534a";

const BASE = 0.12;                       // foundation slab top
// The tent fills the west half; the (+x) yard with the cot is open ground.
const TX = -0.3, TZ = -0.05, TW = 1.2, TD = 1.3;
const WALL_H = 0.48;
const FRONT = TZ + TD / 2;               // the tent's +z face

function tent(h: number): Part {
  return box(TW, h, TD, TENT, [TX, BASE + h / 2, TZ]);
}

// The big flat white roof panel with the red cross laid on top.
function roofPanel(): Part[] {
  const top = BASE + WALL_H + 0.07;
  return [
    box(TW + 0.3, 0.07, TD + 0.3, ROOF_W, [TX, BASE + WALL_H + 0.035, TZ]),
    box(0.72, 0.035, 0.24, CROSS, [TX, top + 0.018, TZ]),
    box(0.24, 0.035, 0.72, CROSS, [TX, top + 0.018, TZ]),
  ];
}

// Timber tent poles at the four wall corners, poking just above the roof.
function tentPoles(): Part[] {
  const out: Part[] = [];
  for (const [x, z] of [[-0.57, -0.62], [0.57, -0.62], [-0.57, 0.62], [0.57, 0.62]] as const) {
    out.push(cyl(0.025, 0.025, 0.62, 5, TIMBER, [TX + x, BASE + 0.31, TZ + z]));
  }
  return out;
}

// Guy ropes staked out on the open east side.
function guyRopes(): Part[] {
  const out: Part[] = [];
  for (const z of [0.0, -0.5]) {
    out.push(cyl(0.008, 0.008, 0.6, 3, CANVAS, [0.6, 0.375, z], [0, 0, 0.53]));
    out.push(box(0.03, 0.08, 0.03, TIMBER, [0.78, BASE + 0.04, z]));
  }
  return out;
}

function entrance(): Part {
  return box(0.3, 0.34, 0.04, SHADOW, [TX - 0.1, BASE + 0.17, FRONT + 0.02]);
}

// The cot in the open yard: timber frame, canvas bed, red blanket, pillow.
function cot(): Part[] {
  const cx = 0.55, cz = 0.42;
  const parts: Part[] = [];
  for (const [dx, dz] of [[-0.22, -0.1], [0.22, -0.1], [-0.22, 0.1], [0.22, 0.1]] as const) {
    parts.push(box(0.04, 0.12, 0.04, TIMBER, [cx + dx, BASE + 0.06, cz + dz]));
  }
  parts.push(box(0.56, 0.035, 0.05, TIMBER, [cx, BASE + 0.13, cz - 0.11]));
  parts.push(box(0.56, 0.035, 0.05, TIMBER, [cx, BASE + 0.13, cz + 0.11]));
  parts.push(box(0.52, 0.045, 0.24, CANVAS, [cx, BASE + 0.15, cz]));
  parts.push(box(0.24, 0.035, 0.26, CROSS, [cx + 0.07, BASE + 0.19, cz]));
  parts.push(box(0.1, 0.04, 0.18, ROOF_W, [cx - 0.22, BASE + 0.19, cz]));
  return parts;
}

export const fieldHospital = defineModel({
  id: "field-hospital",
  tiles: { w: 2, h: 2 },
  batchId: "wwi",
  name: "Field Hospital",
  tags: ["building", "wwi", "military", "hospital", "medical", "tent"],
  summary: "A white hospital tent whose flat roof carries a bold red cross.",
  description:
    "Canvas tent-hospital behind the lines: white walls under a big flat white " +
    "roof panel with a bold red cross read from above, guy ropes staked to the " +
    "east, and a cot in the open yard where medics tend the wounded.",
  author: "Fable 5",
  footprint: 1,
  defaultVariant: "complete",
  variants: [
    { id: "foundation", label: "Foundation", parts: () => [slab(1.94, 1.94)] },
    { id: "lower", label: "Lower", parts: () => [
      slab(1.94, 1.94), tent(0.22), ...poles(1.7, 1.7, 0.6),
    ] },
    { id: "upper", label: "Upper", parts: () => [
      slab(1.94, 1.94), tent(WALL_H), entrance(), ...tentPoles(), ...poles(1.7, 1.7, 1.0),
    ] },
    { id: "complete", label: "Complete", parts: () => [
      slab(1.94, 1.94), tent(WALL_H), ...roofPanel(), ...tentPoles(),
      entrance(), ...guyRopes(), ...cot(),
    ] },
  ],
  animations: [],
  // Where a medic stands to tend the cot: on open ground north of it, turned
  // to face the patient.
  stations: [
    { id: "tend", position: [0.55, BASE, 0.78], facing: Math.PI, description: "Where a medic stands to tend the cot." },
  ],
  interactions: [
    { id: "tend-wounded", label: "Tend the wounded", station: "tend", variants: ["complete"], ...standVisit() },
  ],
});
